import { useEffect, useState, useCallback } from "react";
import api, { fmtRM } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Plus, Trophy, Building2 } from "lucide-react";

const TIER_STYLE = {
  master_agent: "bg-gold-100 text-gold-700 border-gold-500/40",
  senior_agent: "bg-purple-50 text-purple-700 border-purple-200",
  agent: "bg-sky-50 text-sky-700 border-sky-200",
  sub_agent: "bg-slate-100 text-slate-600 border-slate-300",
};

export default function Agents() {
  const { t } = useLang();
  const [agents, setAgents] = useState(null);
  const [branches, setBranches] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", tier: "agent", commission_rate: 5 });

  const load = useCallback(() => {
    api.get("/agents").then((r) => setAgents(r.data)).catch(() => toast.error("Failed"));
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/agents", { ...form, commission_rate: Number(form.commission_rate) });
      toast.success("Agent added");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  if (!agents || !branches) return <div className="flex justify-center h-64 items-center" data-testid="agents-loading"><div className="w-8 h-8 border-4 border-brand-800 border-t-gold-500 rounded-full animate-spin" /></div>;

  const sorted = [...agents].sort((a, b) => b.sales - a.sales);

  return (
    <div className="space-y-5" data-testid="agents-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-sans text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">{t("app.agents")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{agents.length} agents • {branches.length} branches</p>
        </div>
        <Button data-testid="add-agent-button" onClick={() => setOpen(true)} className="bg-brand-800 hover:bg-brand-700 text-sand rounded-full">
          <Plus className="w-4 h-4 mr-1.5" />{t("common.add")} Agent
        </Button>
      </div>

      <Tabs defaultValue="agents">
        <TabsList data-testid="agents-tabs">
          <TabsTrigger value="agents" data-testid="tab-agents">Agents</TabsTrigger>
          <TabsTrigger value="branches" data-testid="tab-branches">Branches</TabsTrigger>
        </TabsList>

        <TabsContent value="agents" className="mt-4">
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {sorted.map((a, i) => (
              <div key={a.agent_id} data-testid={`agent-card-${a.agent_code}`}
                className="pro-card p-5 relative">
                {i === 0 && (
                  <span className="absolute top-4 right-4 inline-flex items-center gap-1 text-[10px] font-mono uppercase text-gold-700">
                    <Trophy className="w-3.5 h-3.5" />Top
                  </span>
                )}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-brand-800 text-sand flex items-center justify-center font-mono text-xs">{a.agent_code}</div>
                  <div>
                    <div className="font-medium text-brand-950">{a.name}</div>
                    <span className={`inline-flex px-2 py-0.5 rounded-full border text-[9px] font-mono uppercase mt-0.5 ${TIER_STYLE[a.tier] || TIER_STYLE.agent}`}>
                      {a.tier.replace(/_/g, " ")}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                  <div className="bg-sand rounded-lg py-2">
                    <div className="font-sans text-sm font-bold tabular-nums text-brand-950">{fmtRM(a.sales)}</div>
                    <div className="text-[9px] font-mono uppercase text-muted-foreground">Sales</div>
                  </div>
                  <div className="bg-sand rounded-lg py-2">
                    <div className="font-sans text-sm font-bold tabular-nums text-brand-950">{a.bookings}</div>
                    <div className="text-[9px] font-mono uppercase text-muted-foreground">Bookings</div>
                  </div>
                  <div className="bg-sand rounded-lg py-2">
                    <div className="font-sans text-sm font-bold tabular-nums text-gold-700">{a.commission_rate}%</div>
                    <div className="text-[9px] font-mono uppercase text-muted-foreground">Comm</div>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground mt-3">{a.branch || "HQ"} • {a.phone}</div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="branches" className="mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            {branches.map((b) => (
              <div key={b.branch_id} data-testid={`branch-card-${b.branch_id}`}
                className="pro-card p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gold-500/15 border border-gold-500/30 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-gold-600" />
                  </div>
                  <div>
                    <div className="font-medium text-brand-950">{b.name}</div>
                    <div className="text-xs text-muted-foreground">{b.city} • Manager: {b.manager}</div>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="text-muted-foreground">Monthly target</span>
                    <span className="font-mono text-brand-950">{fmtRM(b.monthly_target)}</span>
                  </div>
                  <Progress value={55} className="h-2" />
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-agent-dialog" className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-sans font-semibold text-lg">{t("common.add")} Agent</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div>
              <Label>{t("common.name")}</Label>
              <Input data-testid="agent-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("common.phone")}</Label>
                <Input data-testid="agent-phone-input" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <Label>Tier</Label>
                <Select value={form.tier} onValueChange={(v) => setForm({ ...form, tier: v })}>
                  <SelectTrigger data-testid="agent-tier-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["agent", "senior_agent", "master_agent", "sub_agent"].map((s) => (
                      <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Commission Rate (%)</Label>
                <Input data-testid="agent-commission-input" type="number" min="0" step="0.5" value={form.commission_rate} onChange={(e) => setForm({ ...form, commission_rate: e.target.value })} />
              </div>
            </div>
            <Button data-testid="agent-submit-button" type="submit" className="w-full bg-brand-800 hover:bg-brand-700 text-sand rounded-full">{t("common.save")}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
